// shared/samples.ts: the public test ride's sample photo library (docs/DECISIONS.md "Public demo (try.homebuild.ai)").
// On try.homebuild.ai nothing is ever uploaded: every Add Photos opens this library instead, and the Worker copies the
// picked photos into the visitor's own copy (POST /api/projects/:pid/sample-photos). The files are homebuild's own
// fictional images (the demo household photos, a marketing-site photo and crops of generated idea images), in the
// two sizes the upload pipeline stores: public/try/library/<id>.w480.webp and <id>.w1600.webp.
// Each carries the room the photo sort would suggest, so Sort shows a suggestion without an AI call.

export type SamplePhoto = {
  id: string;
  /** The title shown under the photo in the library. */
  title: string;
  /** The suggested room's key (a default room), or null for a photo that is hard to place (Sort asks). */
  room: string | null;
  /** A second room to offer beside the first (or the two chips of a hard one). */
  altRoom: string | null;
  confidence: number;
  caption: string;
  reason: string | null;
  /** The w1600 file's size. */
  width: number;
  height: number;
};

export const SAMPLE_PHOTOS: readonly SamplePhoto[] = [
  { id: "walnut-island", title: "Walnut island", room: "kitchen", altRoom: null, confidence: 0.94, caption: "White kitchen with a walnut-ended island and black pendants", reason: null, width: 1440, height: 1099 },
  { id: "stone-shingle", title: "Stone and shingle", room: "exterior", altRoom: null, confidence: 0.96, caption: "Stone and shingle house with a deep front porch", reason: null, width: 1280, height: 896 },
  { id: "screen-porch", title: "Screened porch", room: "outdoor", altRoom: null, confidence: 0.93, caption: "Screened porch with a long wooden table", reason: null, width: 1600, height: 1195 },
  { id: "dog-wash", title: "Dog wash", room: "mudroom", altRoom: null, confidence: 0.95, caption: "Mudroom with lockers, a bench and a dog wash", reason: null, width: 1360, height: 1076 },
  { id: "lake-room", title: "Room on the lake", room: "great-room", altRoom: "land", confidence: 0.88, caption: "Living room with big windows onto the lake", reason: null, width: 1184, height: 888 },
  { id: "timber-gable", title: "Timber gable", room: "exterior", altRoom: null, confidence: 0.9, caption: "Timber frame house with a glass gable", reason: null, width: 1440, height: 1135 },
  { id: "window-seat", title: "Window seat", room: "office", altRoom: "great-room", confidence: 0.72, caption: "Window seat between built-in shelves", reason: null, width: 1280, height: 960 },
  { id: "coffered-dining", title: "Coffered dining", room: "kitchen", altRoom: "great-room", confidence: 0.81, caption: "Dining room with a coffered ceiling and paneled walls", reason: null, width: 1440, height: 1076 },
  { id: "dusk-barn", title: "Barn at dusk", room: "exterior", altRoom: null, confidence: 0.92, caption: "Board-and-batten house with a metal roof at dusk", reason: null, width: 1248, height: 936 },
  { id: "walkout-slope", title: "Walkout patio", room: "outdoor", altRoom: "lower-level", confidence: 0.8, caption: "Stone patio at the walkout doors", reason: null, width: 1216, height: 912 },
  { id: "wooded-drive", title: "Drive in the woods", room: "land", altRoom: "exterior", confidence: 0.9, caption: "Gravel drive through the woods to the house", reason: null, width: 1152, height: 864 },
  {
    id: "wraparound-porch", title: "Wraparound porch", room: null, altRoom: "exterior", confidence: 0.55, caption: "Shingle house with a wraparound porch",
    reason: "A whole house front, but the porch is the focus.", width: 1472, height: 1135,
  },
];

/** The most photos one pick adds (the library's size: each photo once). */
export const SAMPLE_PICK_MAX = SAMPLE_PHOTOS.length;

export function samplePhoto(id: string): SamplePhoto | undefined {
  return SAMPLE_PHOTOS.find((p) => p.id === id);
}

/** Where a sample's two files are served (static assets). */
export function sampleFile(id: string, variant: "w480" | "w1600"): string {
  return `/try/library/${id}.${variant}.webp`;
}

/**
 * The test ride's sample files for a message (docs/DECISIONS.md "Message attachments"): Attach there offers these instead of
 * the device's files. Two of the builder's plan sheets (public/try/files/<id>.pdf; the Delgados asked for a first-floor
 * primary layout) and two library photos (sent as a photo is: their w1600 and w480).
 */
export type SampleMessageFile = { id: string; name: string; kind: "pdf" | "photo" };
export const SAMPLE_MESSAGE_FILES: readonly SampleMessageFile[] = [
  { id: "first-floor-primary-a", name: "First-Floor Primary, Plan A.pdf", kind: "pdf" },
  { id: "first-floor-primary-b", name: "First-Floor Primary, Plan B.pdf", kind: "pdf" },
  { id: "walnut-island", name: "Walnut island", kind: "photo" },
  { id: "screen-porch", name: "Screened porch", kind: "photo" },
];

export function sampleMessageFile(id: string): SampleMessageFile | undefined {
  return SAMPLE_MESSAGE_FILES.find((f) => f.id === id);
}

/** Where a sample PDF is served from (the static assets). */
export function samplePdfPath(id: string): string {
  return `/try/files/${id}.pdf`;
}
